/**
 * Client-side CSV export for the unified inventory (equipment + component) list.
 * Ported from the legacy Warehouse client and adapted to the real `Item` shape
 * (equipment `service_tag` / component `ref`, nested model/supplier/location).
 * Emits a UTF-8 BOM so Excel renders Vietnamese text correctly. Exports the
 * currently-loaded page rows only — there is no server-side export endpoint.
 */
import dayjs from 'dayjs';
import type { Item } from '@/api/capacities';

/** CSV column: a header label plus a value extractor over an `Item`. */
interface CsvColumn {
  header: string;
  extract: (item: Item) => string;
}

const day = (iso?: string) => (iso ? dayjs(iso).format('YYYY-MM-DD') : '');
const dayTime = (iso?: string) => (iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '');
const num = (n?: number | null) => (n != null ? String(n) : '');

const CSV_COLUMNS: CsvColumn[] = [
  { header: 'Type', extract: (i) => i.item_type || '' },
  { header: 'Name', extract: (i) => i.name || '' },
  { header: 'Reference', extract: (i) => i.service_tag || i.ref || '' },
  { header: 'Category', extract: (i) => i.category || '' },
  { header: 'Status', extract: (i) => i.status?.replace(/_/g, ' ') || '' },
  { header: 'Condition', extract: (i) => i.condition || '' },
  { header: 'Location', extract: (i) => i.current_location?.name || '' },
  { header: 'Supplier', extract: (i) => i.supplier?.name || '' },
  { header: 'Model', extract: (i) => i.model?.name || '' },
  { header: 'Owner', extract: (i) => i.owner || '' },
  { header: 'Quantity', extract: (i) => num(i.quantity) },
  { header: 'Unit', extract: (i) => i.unit || '' },
  { header: 'Purchase Date', extract: (i) => day(i.purchase_date) },
  { header: 'Purchase Price', extract: (i) => num(i.purchase_price) },
  { header: 'Currency', extract: (i) => i.purchase_currency || '' },
  { header: 'Warranty Start', extract: (i) => day(i.warranty_start_date) },
  { header: 'Warranty End', extract: (i) => day(i.warranty_end_date) },
  { header: 'Current Value', extract: (i) => num(i.current_value) },
  { header: 'Created At', extract: (i) => dayTime(i.created_at) },
];

/** Quote a value if it contains a comma, quote or newline (RFC 4180). */
const escapeCsvValue = (value: string): string =>
  /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/** Serialize the given rows to a CSV string (header + one row per item). */
const itemsToCsv = (items: Item[]): string => {
  const header = CSV_COLUMNS.map((c) => escapeCsvValue(c.header)).join(',');
  const rows = items.map((item) =>
    CSV_COLUMNS.map((c) => escapeCsvValue(c.extract(item))).join(','),
  );
  return [header, ...rows].join('\n');
};

/** Trigger a browser download of `csvContent` under `filename` (UTF-8 BOM). */
const downloadCsv = (csvContent: string, filename: string) => {
  const BOM = '﻿';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export the loaded inventory rows to a timestamped CSV and download it.
 * `scope` labels the file (e.g. the active type/category filter). Returns
 * `false` when there is nothing to export.
 */
export const exportItemsCsv = (items: Item[], scope = 'inventory'): boolean => {
  if (items.length === 0) return false;
  const csvContent = itemsToCsv(items);
  const timestamp = dayjs().format('YYYYMMDD_HHmm');
  const safeScope = scope.replace(/[^a-z0-9]+/gi, '_').toLowerCase() || 'inventory';
  downloadCsv(csvContent, `warehouse_${safeScope}_${timestamp}.csv`);
  return true;
};
